// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// Answers: where did Caltex go? Because each station is identifiable over time,
// a change of banner at the same site is visible directly — this is a measured
// flow, not a decomposition dressed up as one.

import { loadRebrands, prefs } from '../data';
import type { RebrandPair } from '../types';
import { chartSvg, mark, svgEl, text } from '../chart';
import { sankey, sankeyPath } from '../layout';
import { categoryColour, esc, num } from '../format';
import { glossaryTerm } from '../glossary';
import { attachSvgZoom } from '../svgZoom';

export async function renderRebrands(root: HTMLElement): Promise<void> {
  root.innerHTML = '<div class="skeleton"></div>';
  const { pairs } = await loadRebrands();
  const usable = pairs.filter((p) => p.flows.length);
  if (!usable.length) {
    root.innerHTML = '<div class="view-head"><h1>Rebrands</h1></div><p class="empty">No station survived long enough under a stable identifier to trace a rebrand.</p>';
    return;
  }
  let idx = Number(prefs.get('rebrandPair', String(usable.length - 1)));
  if (!Number.isFinite(idx) || idx < 0 || idx >= usable.length) idx = usable.length - 1;

  root.innerHTML = `
    <div class="view-head">
      <h1>Where the old brands went</h1>
      <p>Service stations that changed banner without moving — a ${glossaryTerm('rebrand')}.
      Each ribbon is a set of stations that reported under one name in the earlier year and a different one now.</p>
    </div>
    <div class="panel">
      <div class="controls">
        <label for="rb-window">Compare</label>
        <select id="rb-window">${usable.map((p, i) => `<option value="${i}"${i === idx ? ' selected' : ''}>${p.from} → ${p.to}</option>`).join('')}</select>
        <span class="brand-sub" id="rb-summary"></span>
      </div>
      <div class="chart-scroll"><div class="chart-frame" id="rb-frame"></div></div>
      <p class="brand-sub" style="margin-top:var(--space-sm)">
        Stations keeping the same banner are not drawn — only the changes. A station must have reported in
        both years to appear, so closures and new sites are excluded by construction.
      </p>
    </div>
    <div class="panel">
      <div class="panel-head"><h2>Every move</h2><p>The full list behind the diagram.</p></div>
      <div id="rb-table"></div>
    </div>
  `;

  const sel = root.querySelector('#rb-window') as HTMLSelectElement;
  sel.addEventListener('change', () => { idx = Number(sel.value); prefs.set('rebrandPair', String(idx)); draw(); });

  function draw(): void {
    const pair = usable[idx];
    root.querySelector('#rb-summary')!.textContent =
      `${num(pair.changed)} stations changed banner; ${num(pair.stable)} kept theirs`;
    const frame = root.querySelector('#rb-frame') as HTMLElement;
    frame.innerHTML = '';
    frame.appendChild(buildSankey(pair, frame));
    renderTable(root.querySelector('#rb-table')!, pair);
  }
  draw();
}

function buildSankey(pair: RebrandPair, host: HTMLElement): SVGSVGElement {
  const flows = pair.flows.map((f) => ({ source: f.source || 'Unbranded', target: f.target || 'Unbranded', value: f.value }));
  // Sized to the panel, so the diagram fills a desktop layout rather than
  // hugging the left edge; it scrolls locally on anything narrower.
  const width = Math.max(820, Math.min(1240, (host.clientWidth || 900) - 34));
  const padL = 158;
  const padR = 168;
  const padT = 30;
  const padB = 20;
  const innerW = width - padL - padR;
  const innerH = Math.max(340, flows.length * 22);
  const height = innerH + padT + padB;

  const layout = sankey(flows, innerW, innerH, { nodeWidth: 13, gap: 7 });
  const svg = chartSvg(width, height, `Station rebrands between ${pair.from} and ${pair.to}`);
  svg.appendChild(text(padL, 16, String(pair.from), { 'font-size': 12, 'font-weight': 650 }));
  svg.appendChild(text(padL + innerW, 16, String(pair.to), { 'font-size': 12, 'font-weight': 650, 'text-anchor': 'end' }));

  const nodeAt = (side: 'source' | 'target', id: string) => layout.nodes.find((n) => n.side === side && n.id === id)!;

  for (const link of layout.links) {
    const sn = nodeAt('source', link.source);
    const tn = nodeAt('target', link.target);
    const path = svgEl('path', {
      d: sankeyPath(padL + sn.x + sn.w, padT + link.y0, padL + tn.x, padT + link.y1),
      fill: 'none',
      stroke: categoryColour(link.source),
      'stroke-width': Math.max(1, link.thickness),
      'stroke-opacity': 0.42,
    });
    mark(path, `${link.source} → ${link.target}\n${num(link.value)} stations`);
    path.addEventListener('mouseenter', () => path.setAttribute('stroke-opacity', '0.78'));
    path.addEventListener('mouseleave', () => path.setAttribute('stroke-opacity', '0.42'));
    svg.appendChild(path);
  }

  for (const n of layout.nodes) {
    const rect = svgEl('rect', {
      x: padL + n.x, y: padT + n.y, width: n.w, height: Math.max(1.5, n.h), rx: 2,
      fill: categoryColour(n.id),
    });
    mark(rect, `${n.id}\n${num(n.value)} stations ${n.side === 'source' ? 'left' : 'gained'} this banner`);
    svg.appendChild(rect);
    const label = text(
      n.side === 'source' ? padL + n.x - 7 : padL + n.x + n.w + 7,
      padT + n.y + Math.max(1.5, n.h) / 2 + 4,
      `${n.id.length > 20 ? `${n.id.slice(0, 19)}…` : n.id} (${n.value})`,
      { 'font-size': 11.5, 'text-anchor': n.side === 'source' ? 'end' : 'start' },
    );
    svg.appendChild(label);
  }
  const frameless = svgEl('g');
  svg.appendChild(frameless);
  window.setTimeout(() => attachSvgZoom(svg, { maxScale: 4 }), 0);
  return svg;
}

function renderTable(host: HTMLElement, pair: RebrandPair): void {
  host.innerHTML = `
    <div class="table-scroll">
      <table>
        <thead><tr><th class="n">#</th><th>Was (${pair.from})</th><th>Now (${pair.to})</th><th class="n">Stations</th></tr></thead>
        <tbody>${pair.flows.map((f, i) => `<tr>
          <td class="n rank">${i + 1}</td>
          <td>${esc(f.source || 'Unbranded')}</td>
          <td>${esc(f.target || 'Unbranded')}</td>
          <td class="n">${num(f.value)}</td>
        </tr>`).join('')}</tbody>
      </table>
    </div>
  `;
}
